import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { 
  CheckCircle, 
  Home
} from '@mui/icons-material';
import { apiService } from '../services/api';

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Process the payment success and complete bid acceptance or onboarding
    const processPaymentSuccess = async () => {
      try {
        // Check if this is onboarding or bid acceptance
        const bidId = sessionStorage.getItem('pendingBidId');
        const customerId = sessionStorage.getItem('pendingCustomerId');
        const isOnboarding = sessionStorage.getItem('isOnboarding') === 'true';
        const sessionId = searchParams.get('session_id');
        
        if (isOnboarding) {
          console.log('Processing payment success for onboarding');
          console.log('Session ID from URL:', sessionId);
          
          // NEW: Get existingCustomerId from session storage
          const onboardingExistingCustomerId = sessionStorage.getItem('onboardingExistingCustomerId');
          
          // If we have a session_id, we need to get the setup_intent from the checkout session
          let setupIntentId: string | null = null;
          if (sessionId) {
            try {
              const sessionData = await apiService.get<any>('/payments/get-checkout-session', { sessionId });
              console.log('Checkout session data:', sessionData);
              
              if (sessionData.success && sessionData.data && 'setup_intent' in sessionData.data) {
                setupIntentId = sessionData.data.setup_intent as string;
                console.log('Got setup_intent from checkout session:', setupIntentId);
              } else {
                console.warn('No setup_intent in checkout session response:', sessionData);
              }
            } catch (err: any) {
              console.error('Failed to get checkout session:', err);
              // Continue without setupIntentId - the endpoint can handle it
            }
          } else {
            console.warn('No session_id in URL parameters');
          }
          
          // Call the onboarding payment confirmed endpoint - pass existingCustomerId
          console.log('Calling payment-confirmed endpoint with setupIntentId and existingCustomerId:', {
            setupIntentId,
            existingCustomerId: onboardingExistingCustomerId,
          });
          const result = await apiService.post('/existing-customers/onboard/payment-confirmed', {
            setupIntentId,
            existingCustomerId: onboardingExistingCustomerId, // NEW: Pass property ID
          });
          console.log('Payment confirmed response:', result);
          
          if (result.success) {
            console.log('Onboarding payment confirmed successfully:', result);
            sessionStorage.removeItem('isOnboarding');
            sessionStorage.removeItem('onboardingExistingCustomerId'); // NEW: Clean up
          } else {
            throw new Error(result.error || result.message || 'Failed to complete onboarding payment');
          }
        } else if (bidId && customerId) {
          console.log('Processing payment success for bid:', { bidId, customerId });
          
          // Call the payment success endpoint to complete bid acceptance
          const result = await apiService.post('/payments/payment-success', { bidId, customerId });
          
          if (result.success) {
            console.log('Bid accepted successfully:', result);
            // Clear the session storage after successful processing
            sessionStorage.removeItem('pendingBidId');
            sessionStorage.removeItem('pendingCustomerId');
          } else {
            throw new Error(result.error || 'Failed to complete bid acceptance');
          }
        } else {
          console.log('No bid ID or customer ID found in session storage, and not onboarding');
          // This might be a general payment setup (e.g., for an already-onboarded customer)
          // Manually process the checkout session to save the payment method immediately
          const sessionId = searchParams.get('session_id');
          if (sessionId) {
            console.log('Processing checkout session manually:', sessionId);
            try {
              const processResult = await apiService.post<any>('/payments/process-checkout-session', { sessionId });
              if (processResult.success) {
                console.log('Payment method saved successfully via manual processing:', processResult);
              } else {
                console.warn('Failed to process checkout session manually:', processResult.error);
                // Continue anyway - webhook might process it
              }
            } catch (err: any) {
              console.error('Error processing checkout session manually:', err);
              // Continue anyway - webhook might process it
            }
          } else {
            console.log('No session_id in URL - payment method should be saved via webhook');
          }
        }
        
        setLoading(false);
      } catch (err: any) {
        console.error('Error processing payment success:', err);
        const errorMessage = err?.message || err?.error || 'There was an error processing your payment. Please contact support.';
        setError(errorMessage);
        setLoading(false);
      }
    };

    processPaymentSuccess();
  }, [searchParams]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoToDashboard = () => {
    navigate('/customer?paymentSuccess=true');
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Processing your payment...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : (
            <>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom>
                Payment Setup Complete!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your payment method has been successfully set up and your service request has been accepted. Your provider will contact you to schedule your services.
              </Typography>
            </>
          )}
          
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleGoToDashboard}
              startIcon={<Home />}
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outlined"
              onClick={handleGoHome}
            >
              Go Home
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentSuccessPage;